import React, { useState, useCallback, useEffect, useRef } from 'react'
import { Modal, Form, message, Radio, Spin, Input, Select, InputNumber, DatePicker } from 'antd'
import { queryCustomerSalesInfoByName, getCustomerInfo } from '@services/customer'
import { CustomerSalesInfo, ContactInfoItem, CustomerInfo, needTotalEnum } from '@types'
import { debounce } from 'lodash'
import { AreaCascader, CommonSelector, CommonCascador, CommonAutoComplete } from '@components'
import { transferToDianShiFormOptions } from '../config'
import { leadListByCid, leadTags, industryList } from 'src/components/CommonSelector/configs'
import { customerTypeMap } from '@configs/map'
import { areaTree } from '@configs/area'
import { leadIntentionLevel, industryProductsOnlyValid } from 'src/components/CommonCascador/configs'
import { addSDRIntentionLead, getLeadPoolList, getSDRIntentionLeadDetail } from '@services/lead'
import moment from 'moment'
import { searchCustomer } from 'src/components/CommonAutoComplete/configs'
import { findProvinceAndCityValues, getArea } from '@library'
import AiCustomerPersonas from '@pages/customer/detail/pages/detail-tabs/AiCustomerPersonas'
import { globalStore, sessionStore } from '@store'
import { moduleId } from '@configs'

import './IntentionLeadModal.less'
import { useContactInfoService } from '../hooks'

interface IntentionLeadModalProps {
  open: boolean
  onCancel?: () => void
  onOk?: () => void
  // 创建模式props
  mode?: 'create' | 'edit'
  // 编辑模式props

  customerDetail?: {
    cid?: string
    customerInfo?: CustomerInfo
    contactInfoList?: ContactInfoItem[]
  }
  editDetail?: {
    leadId?: number
    customerId?: number
    origLeadId?: number
    cid?: string
  }
}

const IntentionLeadModal = ({
  open,
  onCancel,
  onOk,
  mode = 'create',
  customerDetail,
  editDetail,

}: IntentionLeadModalProps) => {
  const authPointMap = globalStore(s => s.authPointMap)
  const userInfo = sessionStore(s => s.userInfo)
  const { cid: propsCid, customerInfo: propsCustomerInfo, contactInfoList: propsContactInfoList } = customerDetail || {}
  const { leadId, customerId, cid: editCid, origLeadId: editOrigLeadId } = editDetail || {}
  // 状态管理
  const cid = mode === 'create' ? propsCid : editCid
  const [contactInfoList, setContactInfoList] = useState<ContactInfoItem[]>([])
  const [dataLoading, setDataLoading] = useState(false)

  // 在编辑模式下，通过customerId获取客户信息时使用的cid
  const [customerDataCid, setCustomerDataCid] = useState<string>('')
  const [, hookContactInfoList] = useContactInfoService(customerDataCid)

  const [form] = Form.useForm()
  const customerName = Form.useWatch('customerName', form)
  const contactId = Form.useWatch('contactId', form)
  const monthConsumption = Form.useWatch('monthConsumption', form)
  const position = Form.useWatch('position', form)
  const origLeadId = Form.useWatch('origLeadId', form)
  const businessDemand = Form.useWatch('businessDemand', form)

  const [customerInfo, setCustomerInfo] = useState<CustomerSalesInfo | null>(null)
  const [contactInfo, setContactInfo] = useState<ContactInfoItem | null>(null)
  const [loading, setLoading] = useState(false)
  const [initFetchCustomerInfo, setInitFetchCustomerInfo] = useState(false)
  const [initCustomerInfo, setInitCustomerInfo] = useState<CustomerSalesInfo | null>(null)
  // 跟踪编辑模式下是否是第一次渲染
  const isFirstRenderInEditMode = useRef(mode === 'edit')

  // 初始化数据
  const initializeData = useCallback(async () => {
    if (!open) return
    setDataLoading(true)
    try {
      if (mode === 'create') {
        // 创建模式：使用props传入的数据
        if (propsContactInfoList) {
          setContactInfoList(propsContactInfoList)
        }
      } else if (mode === 'edit') {
        // 编辑模式：通过customerId获取客户信息
        // if (customerId) {
        //   const [customerData, customerErr] = await getCustomerInfo(customerId)
        //   if (customerErr) {
        //     message.error(`获取客户信息失败(${customerErr.message})`)
        //     return
        //   }
        //   // 设置cid以触发useContactInfoService Hook
        //   setCustomerDataCid(customerData.cid)
        // }
        // 编辑模式：通过origLeadId获取原始客户cid，解决在创建意向转出时，变更了客户名称后，用新客户名称的cid查出来的联系人列表无法匹配到当前联系人的问题
        // 现在改为通过线索id查询之前的cid，然后使用cid获取联系人列表，这样可以跟创建时候那个联系人列表对应上
        if (editOrigLeadId) {
          const [customerData, customerErr] = await getLeadPoolList({
            leadIds: [editOrigLeadId],
            page: 1,
            pageSize: 10,
            needTotal: needTotalEnum.LIST,
          })
          if (customerErr) {
            message.error(`获取客户信息失败(${customerErr.message})`)
            return
          }
          // 设置cid以触发useContactInfoService Hook
          setCustomerDataCid(customerData?.list?.[0].cid)
        }

        // 加载线索详情
        if (leadId) {
          const [leadDataRes, leadErr] = await getSDRIntentionLeadDetail({ intentionLeadId: leadId })
          const leadData = leadDataRes?.info
          if (leadErr) {
            message.error(`获取线索详情失败(${leadErr.message})`)
            return
          }

          const area = getArea(leadData?.city, leadData?.province)?.split('-')
          const intention = leadData?.intentionLevel && leadData?.intentionSecondLevel
            ? [leadData.intentionLevel, leadData.intentionSecondLevel]
            : undefined
          const needProduct = leadData?.needProduct1 && leadData?.needProduct2
            ? [leadData.needProduct1, leadData.needProduct2]
            : undefined

          form.setFieldsValue({
            leadSource: leadData?.leadSource,
            origLeadId: leadData?.origLeadId,
            customerName: leadData?.customerName,
            cid: leadData?.cid,
            salesChannel: leadData?.salesChannel,
            nameSales: leadData?.nameSales,
            customerType: leadData?.customerType,
            district: leadData?.district,
            area,
            firstIncomeIndustryId: leadData?.firstIncomeIndustryId,
            intention,
            needProduct,
            leadTags: leadData?.leadTagCode,
            expectBuyDate: leadData?.expectBuyDate ? moment(leadData.expectBuyDate) : undefined,
            registeredCapital: leadData?.registeredCapital,
            memberScale: leadData?.memberScale,
            monthConsumption: leadData?.monthConsumption,
            contactId: leadData?.contactId,
            contactName: leadData?.contactName,
            contactPosition: leadData?.contactPosition,
            contactPhone: leadData?.contactPhone,
            position: leadData?.position,
            businessDemand: leadData?.businessDemand,
            cloudVendorUse: leadData?.cloudVendorUse,
          })
        }
      }
    } finally {
      setDataLoading(false)
    }
  }, [open, mode, propsContactInfoList, customerId, leadId, form])

  const fetchCustomerInfo = useCallback(
    debounce(async (name) => {
      if (!name) return
      setLoading(true)
      try {
        const params = { name }
        const [data, err] = await queryCustomerSalesInfoByName(params)
        if (!initFetchCustomerInfo) {
          setInitCustomerInfo(data)
          setInitFetchCustomerInfo(true)
        }
        if (err) {
          message.error(`获取客户销售信息失败(${err.message})`)
          setCustomerInfo(initCustomerInfo)
          const area = findProvinceAndCityValues(areaTree, initCustomerInfo?.province, initCustomerInfo?.city)
          form.setFieldsValue({
            cid,
            salesChannel: initCustomerInfo?.salesChannel,
            salesChannelName: initCustomerInfo?.salesChannelName,
            nameSales: initCustomerInfo?.nameSales,
            area,
            firstIncomeIndustryId: initCustomerInfo?.incomeIndustry?.firstIncomeIndustry?.id,
            registeredCapital: initCustomerInfo?.registeredCapital,
          })
          return
        }
        setCustomerInfo(data)
        const area = findProvinceAndCityValues(areaTree, data?.province, data?.city)
        form.setFieldsValue({
          cid: data?.cid,
          salesChannel: data?.salesChannel,
          nameSales: data?.nameSales,
          area,
          firstIncomeIndustryId: data?.incomeIndustry?.firstIncomeIndustry?.id,
          registeredCapital: data?.registeredCapital,
        })
      } finally {
        setLoading(false)
      }
    }, 1000),
    [form, initFetchCustomerInfo, initCustomerInfo, cid],
  )

  // 初始化数据
  useEffect(() => {
    initializeData()
  }, [open, editDetail])

  // 当hookContactInfoList更新时，设置联系人列表
  useEffect(() => {
    if (hookContactInfoList && hookContactInfoList.length > 0) {
      setContactInfoList(hookContactInfoList)
    }
  }, [hookContactInfoList])

  useEffect(() => {
    // 创建模式下直接调用
    if (mode === 'create') {
      fetchCustomerInfo(customerName)
    } else if (mode === 'edit' && !isFirstRenderInEditMode.current) {
      fetchCustomerInfo(customerName)
    }
    // 如果是编辑模式的第一次渲染，将标记设为 false
    if (mode === 'edit' && isFirstRenderInEditMode.current) {
      isFirstRenderInEditMode.current = false
    }

    return () => {
      fetchCustomerInfo.cancel()
    }
  }, [customerName, fetchCustomerInfo, mode])
  useEffect(() => {
    const contact = contactInfoList?.find(item => item.contactId === contactId)
    if (contact) {
      setContactInfo(contact)
      form.setFieldsValue({
        contactId: contact?.contactId,
        contactName: contact?.name,
        contactPosition: contact?.title,
        contactPhone: contact?.phone,
      })
    }
  }, [contactId])
  const handleOk = () => {
    form.validateFields().then(async (values) => {
      const payload = {
        ...values,
        expectBuyDate: values?.expectBuyDate ? moment(values?.expectBuyDate)?.format('YYYY-MM-DD') : undefined,
        province: values?.area?.[0],
        city: values?.area?.[1],
        intentionLevel: values?.intention?.[0],
        intentionSecondLevel: values?.intention?.[1],
        needProduct1: values?.needProduct?.[0],
        needProduct2: values?.needProduct?.[1],
        leadSource: mode === 'create' ? 1 : values.leadSource,
      }

      if (mode === 'edit') {
        payload.leadId = leadId
        const [, err] = await addSDRIntentionLead(payload)
        if (err) {
          message.error(`保存失败(${err.message})`)
          return
        }
        message.success('保存成功')
      } else {
        const [, err] = await addSDRIntentionLead(payload)
        if (err) {
          message.error(`保存失败(${err.message})`)
          return
        }
        message.success('保存成功')
      }

      form.resetFields()
      onOk && onOk()
    })
  }

  const handleCancel = () => {
    form.resetFields()
    onCancel && onCancel()
  }

  const handleModalChange = (data) => {
    form.setFieldsValue({
      businessDemand: data?.businessDemand ? `【需求场景】${data?.businessDemand}\n【项目阶段】${data?.projectPhases}\n【商务对接】${data?.businessMatchmaking}` : businessDemand,
      monthConsumption: data ? data?.monthConsumption : monthConsumption,
      position: data ? transferToDianShiFormOptions.position?.find(m => m === data?.position) || position : position,
    })
  }

  const modalTitle = mode === 'edit' ? '编辑意向线索' : '意向线索'

  return (
    <Modal
      open={open}
      width={1200}
      onCancel={handleCancel}
      onOk={handleOk}
      title={modalTitle}
    >
      <Spin spinning={dataLoading}>
        <div className="flex-modal-container">
          <Form
            className="flex-modal-left"
            scrollToFirstError
            autoComplete="off"
            form={form}
            labelWrap
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 20 }}
          >
            <Form.Item label="线索来源" name="leadSource" initialValue={1} rules={[{ required: true, message: '不能为空' }]}>
              <Radio.Group buttonStyle="solid" value={1}>
                <Radio.Button value={1} defaultChecked>线索平台</Radio.Button>
              </Radio.Group>
            </Form.Item>
            <Form.Item label="原线索" name="origLeadId" rules={[{ required: true, message: '不能为空' }]}>
              <CommonSelector onChanges={(value, wholeValue) => {
                // 根据线索里的公司规模设置公司规模字段
                const memberScale = wholeValue.find(item => item?.leadId === value)?.leadExtend?.find(item => item.key === 'CompanyScale')?.keyValue
                if (transferToDianShiFormOptions.memberScale.includes(memberScale)) {
                  form.setFieldsValue({
                    memberScale,
                  })
                } else {
                  form.setFieldsValue({
                    memberScale: undefined,
                  })
                }
              }} {...leadListByCid} filter={{ cid, excludeStatus: 6 }} />
            </Form.Item>
            <Form.Item initialValue={propsCustomerInfo?.name} label="客户名称" name="customerName" rules={[{ required: true, message: '不能为空' }]}>
              <CommonAutoComplete {...searchCustomer} />
            </Form.Item>
            <Form.Item label="CID" name="cid" initialValue={cid} dependencies={['customerName']}>
              <Input readOnly bordered={false} />
            </Form.Item>
            <Form.Item label="销售通路" name="salesChannel" dependencies={['customerName']} getValueProps={(value) => ({
              value: customerInfo?.salesChannelName || value,
            })} normalize={() => customerInfo?.salesChannel}>
              {loading ? <Spin size="small" /> : <Input readOnly bordered={false} />}
            </Form.Item>
            <Form.Item label="name商务" name="nameSales" dependencies={['customerName']}>
              {loading ? <Spin size="small" /> : <Input readOnly bordered={false} />}
            </Form.Item>
            <Form.Item label="客户类型" name="customerType" initialValue={1} rules={[{ required: true, message: '不能为空' }]}>
              <Radio.Group buttonStyle="solid">
                {Object.entries(customerTypeMap).slice(0, 2).map(([value, text]) => {
                  const temp = +value
                  const val = isNaN(temp) ? value : temp
                  return <Radio.Button key={value} value={val}>{text}</Radio.Button>
                })}
              </Radio.Group>
            </Form.Item>
            <Form.Item label="客户所在区域" name="district" initialValue="无">
              <Radio.Group buttonStyle="solid">{transferToDianShiFormOptions.districtWithEmpty.map(item => <Radio.Button key={item} value={item}>{item}</Radio.Button>)}</Radio.Group>
            </Form.Item>
            <Form.Item label="客户所在省市" name="area">
              <AreaCascader />
            </Form.Item>
            <Form.Item label="客户所属行业" name="firstIncomeIndustryId" rules={[{ required: true, message: '不能为空' }]}>
              <CommonSelector {...industryList} valueKey="nodeCode" />
            </Form.Item>
            <Form.Item label="客户意向" name="intention" rules={[{ required: true, message: '不能为空' }]}>
              <CommonCascador {...leadIntentionLevel} />
            </Form.Item>
            <Form.Item label="关注产品" name="needProduct" rules={[{ required: true, message: '不能为空' }]}>
              <CommonCascador {...industryProductsOnlyValid} />
            </Form.Item>
            <Form.Item label="线索标签" name="leadTags" rules={[{ required: true, message: '不能为空' }]}>
              <CommonSelector mode="multiple" {...leadTags} />
            </Form.Item>
            <Form.Item label="意向采购时间" name="expectBuyDate">
              <DatePicker />
              {/* <Select>{transferToDianShiFormOptions.expectBuyDate.map(item => <Select.Option key={item} value={item}>{item}</Select.Option>)}</Select> */}
            </Form.Item>
            <Form.Item label="注册资本（人民币万元）" name="registeredCapital" rules={[{ required: true, message: '不能为空' }]}>
              <InputNumber precision={0} min={0} max={99999999999999} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="公司规模" name="memberScale">
              <Select>{transferToDianShiFormOptions.memberScale.map(item => <Select.Option key={item} value={item}>{item}</Select.Option>)}</Select>
            </Form.Item>
            <Form.Item label="IT资金规模(月消耗)" name="monthConsumption">
              <Select>{transferToDianShiFormOptions.monthConsumption.map(item => <Select.Option key={item} value={item}>{item}</Select.Option>)}</Select>
            </Form.Item>
            <Form.Item label="联系人姓名" name="contactId" rules={[{ required: true, message: '不能为空' }]}>
              <Select
                onClick={() => {
                  if (contactInfoList?.length === 0) {
                    message.error('未查询到客户名下联系人信息，请在客户详情页底部添加联系人信息')
                  }
                }} placeholder="请选择联系人"
                onChange={(value) => {
                  const selectedContact = contactInfoList?.find(item => item.contactId === value)
                  if (selectedContact && (!selectedContact.name || !selectedContact.phone || !selectedContact.title)) {
                    message.error('所选联系人信息不能为空，请至底部联系人信息补全')
                  }
                }}>{contactInfoList?.map(item => <Select.Option key={item.contactId} value={item.contactId}>{(contactId && contactId === item.contactId) ? contactInfo?.name : `${item?.name || '无姓名'}/${item?.phone || '无电话'}/${item.title || '无职位'}`}</Select.Option>)}
              </Select>
            </Form.Item>
            <Form.Item label="联系人职务" name="contactPosition" dependencies={['contactId']} rules={[{ required: true, message: '不能为空' }]}>
              <Input readOnly bordered={false} />
            </Form.Item>
            <Form.Item label="联系人电话" name="contactPhone" dependencies={['contactId']} rules={[{ required: true, message: '不能为空' }]}>
              <Input readOnly bordered={false} />
            </Form.Item>
            <Form.Item label="联系人身份" name="position" rules={[{ required: true, message: '不能为空' }]}>
              <Radio.Group buttonStyle="solid">{transferToDianShiFormOptions.position.map(item => <Radio.Button key={item} value={item}>{item}</Radio.Button>)}</Radio.Group>
            </Form.Item>
            <Form.Item label="客户需求" name="businessDemand" rules={[{ required: true, message: '不能为空' }]}>
              <Input.TextArea autoSize={{ minRows: 4, maxRows: 4 }} allowClear placeholder="请输入" />
            </Form.Item>
            <Form.Item label="云厂商使用情况" name="cloudVendorUse">
              <Select mode="multiple" allowClear>{transferToDianShiFormOptions.cloudVendorUse.map(item => <Select.Option key={item} value={item}>{item}</Select.Option>)}</Select>
            </Form.Item>
          </Form>
          <div className="divider" />
          {/* AI意向提炼 */}
          {authPointMap.tccPermissionAiLeadTransferExt1?.result && [moduleId.SDR_CLEAN].includes(userInfo.belongModule) && (
            <div className="flex-modal-right">
              <AiCustomerPersonas title="💡AI意向提炼" type="intention" expandable={false} id={origLeadId} onChange={handleModalChange} />
            </div>
          )}
        </div>
      </Spin>
    </Modal>
  )
}

export default IntentionLeadModal
